/**
 * Project case studies — featured installs with photos, services, and outcomes.
 * Used by:
 *   - src/routes/case-studies.tsx (index)
 *   - src/routes/case-studies.$slug.tsx (detail)
 *
 * Imagery is sourced from the existing gallery in src/assets/. Add new entries
 * by appending to CASE_STUDIES — the index page and dynamic route pick them
 * up automatically.
 */

import sanMarketStorefront from "@/assets/san-market-green-stripe-awning-night.jpg";
import sanMarketInstall from "@/assets/san-market-awning-install-ladder.jpg";
import sanMarketFrame from "@/assets/san-market-awning-frame-underside.jpg";

import comalFlowerAngled from "@/assets/comal-flower-shop-storefront-awnings.jpg";
import comalFlowerFront from "@/assets/comal-flower-shop-front-elevation.jpg";

import bradzoilCanopy from "@/assets/bradzoil-yellow-canopy-awning.jpg";
import bradzoil988 from "@/assets/bradzoil-988-storefront-awning.png";
import bradzoilBanner from "@/assets/awning-bradzoil-banner.jpg";

import tuffShed from "@/assets/tuff-shed-awning.jpg";
import handPaintedLogo from "@/assets/hand-painted-logo-awning.jpg";

import sailBefore from "@/assets/sail-shade-before-red.jpg";
import sailAfter from "@/assets/sail-shade-after-black.jpg";
import sailUnderside from "@/assets/sail-shade-blue-underside.jpg";

import blackBoatLakehouse from "@/assets/boat-cover-black-lakehouse.jpg";
import blackBoatSide from "@/assets/boat-cover-black-side-trailer.jpg";
import blackBoatRear from "@/assets/boat-cover-black-rear.jpg";

import tanRanchTrailer from "@/assets/tan-stock-trailer-cover-texas-ranch.jpg";
import tanLongGooseneck from "@/assets/stock-trailer-tan-long-gooseneck.jpg";
import grayLacingCorner from "@/assets/stock-trailer-gray-lacing-corner.jpg";

import mainStreetAwning from "@/assets/awning-400-s-main.jpg";
import mainDome from "@/assets/400-s-main-dome-awning-shop.jpg";

export interface CaseStudyImage {
  src: string;
  alt: string;
  caption?: string;
}

export interface CaseStudy {
  slug: string;
  title: string;
  client: string;
  location: string;
  service: string;
  serviceHref: string;
  cityHref?: string;
  category:
    | "Awnings"
    | "Commercial Awnings"
    | "Sail Shades"
    | "Boat Covers"
    | "Stock Trailer Covers"
    | "Patio Covers";
  /** Short sentence used on cards + meta description. */
  summary: string;
  /** 2–3 paragraph project overview. */
  overview: string[];
  /** Bulleted list of work performed. */
  scope: string[];
  /** Materials & specs callout. */
  specs: { label: string; value: string }[];
  /** Outcome bullets — what the client got. */
  outcomes: string[];
  /** Photos. First is the hero. */
  images: CaseStudyImage[];
  /** Optional pull-quote testimonial. */
  testimonial?: { quote: string; author: string; role?: string };
  /** Year the work was completed. */
  year: number;
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "san-market-downtown-san-marcos",
    title: "The San Market — Green-Stripe Storefront Awning, Downtown San Marcos",
    client: "The San Market",
    location: "San Marcos, TX",
    service: "Commercial Awnings",
    serviceHref: "/commercial-awnings",
    cityHref: "/san-marcos",
    category: "Commercial Awnings",
    year: 2024,
    summary:
      "Custom green-and-white striped Sunbrella® storefront awning over a TIG-welded aluminum frame for The San Market gift shop on the downtown San Marcos plaza.",
    overview: [
      "The San Market wanted curb appeal that matched the historic downtown plaza without sacrificing daily-use durability. We patterned the storefront on-site, fabricated a TIG-welded aluminum frame in our Seguin shop, and sewed the green-and-white Sunbrella canvas in-house.",
      "The install was scheduled around the shop's business hours so they didn't lose a single day of foot traffic. Crew lifted the welded frame into place, stretched the canvas tight, and dialed in the lacing in a single afternoon.",
    ],
    scope: [
      "On-site patterning of storefront elevation",
      "TIG-welded aluminum awning frame fabricated in Seguin",
      "Custom Sunbrella® green-and-white striped canvas, sewn in-house",
      "Canvas stretched and laced over the welded frame",
      "Single-day install with zero shop downtime",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® green & white awning stripe" },
      { label: "Frame", value: "TIG-welded aluminum tube" },
      { label: "Mount", value: "Through-bolted to storefront facade" },
      { label: "Service Area", value: "San Marcos / Hays County" },
      { label: "Timeline", value: "~2 weeks shop, 1 day install" },
    ],
    outcomes: [
      "Bold downtown storefront presence that matches the historic plaza",
      "Welded aluminum frame engineered for 20+ years of daily use",
      "UV- and mildew-resistant canvas built for direct Texas sun",
      "Zero business downtime during install",
    ],
    images: [
      { src: sanMarketStorefront, alt: "The San Market green-and-white striped storefront awning installed by Texas Made Shade in downtown San Marcos at night", caption: "Finished storefront — downtown San Marcos at night" },
      { src: sanMarketInstall, alt: "Texas Made Shade installer on a ladder fitting striped canvas to a welded aluminum frame at The San Market", caption: "On-site install — canvas stretched onto the welded frame" },
      { src: sanMarketFrame, alt: "Underside view of the TIG-welded aluminum awning frame with green and white striped canvas at The San Market", caption: "Welded aluminum frame underside — built in Seguin" },
    ],
  },
  {
    slug: "comal-flower-shop-new-braunfels",
    title: "Comal Flower Shop — Matched Storefront & Window Awnings, New Braunfels Plaza",
    client: "Comal Flower Shop",
    location: "New Braunfels, TX",
    service: "Commercial Awnings",
    serviceHref: "/commercial-awnings",
    cityHref: "/new-braunfels",
    category: "Commercial Awnings",
    year: 2024,
    summary:
      "Set of custom green-and-cream striped Sunbrella® storefront and matching window awnings across a brick storefront on the New Braunfels Plaza.",
    overview: [
      "Comal Flower Shop needed a coordinated awning set that read as a single storefront across multiple bays and windows. We measured every elevation, drew up a panel-by-panel layout, and fabricated the welded aluminum frames so each awning hung perfectly true to the brick coursing.",
      "Sunbrella green-and-cream stripe was selected to match the shop's brand. Each panel was sewn in-house and hand-installed across two days on the historic Plaza.",
    ],
    scope: [
      "Storefront elevation measurement across multiple bays",
      "Welded aluminum frames sized to brick coursing",
      "Coordinated Sunbrella® stripe panels for storefront + windows",
      "Two-day install on the historic New Braunfels Plaza",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® green & cream awning stripe" },
      { label: "Frames", value: "Welded aluminum (storefront + window panels)" },
      { label: "Panel Count", value: "Multiple coordinated awnings" },
      { label: "Service Area", value: "New Braunfels / Comal County" },
      { label: "Timeline", value: "~3 weeks shop, 2 day install" },
    ],
    outcomes: [
      "Cohesive branded storefront across multiple window bays",
      "Historic plaza-appropriate finish over a brick facade",
      "Premium Sunbrella canvas warranty for long Texas exposure",
    ],
    images: [
      { src: comalFlowerAngled, alt: "Set of green-and-cream striped Sunbrella storefront and window awnings installed by Texas Made Shade over Comal Flower Shop in downtown New Braunfels", caption: "Comal Flower Shop — angled view of full set" },
      { src: comalFlowerFront, alt: "Front-elevation view of multiple custom green-and-cream striped Sunbrella commercial awnings across the Comal Flower Shop brick storefront", caption: "Front elevation — matched storefront & window awnings" },
    ],
  },
  {
    slug: "bradzoil-988-branded-canopy",
    title: "Bradzoil 10 Minute Oil Change — Branded Yellow Canopy & Hand-Painted Logo",
    client: "Bradzoil 10 Minute Oil Change",
    location: "South Texas",
    service: "Commercial Awnings",
    serviceHref: "/commercial-awnings",
    category: "Commercial Awnings",
    year: 2024,
    summary:
      "Bright yellow Sunbrella® storefront canopy with hand-painted Bradzoil logo and 988 street number — built and branded entirely in-house.",
    overview: [
      "Bradzoil wanted an awning that doubled as storefront signage. We fabricated a welded aluminum service-bay canopy, finished it with a bright yellow Sunbrella canvas top, and hand-painted the Bradzoil 10 Minute Oil Change logo and 988 street number directly onto the canvas using commercial-grade paints built for the Texas sun.",
      "The result is a high-visibility branded structure that drives recognition from the road and shades customers at the bay entrance.",
    ],
    scope: [
      "Welded aluminum service-bay canopy frame",
      "Bright yellow Sunbrella® commercial canvas",
      "Hand-painted Bradzoil logo and 988 street number",
      "Same-crew fabrication, painting and installation",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® yellow commercial canvas" },
      { label: "Frame", value: "Welded aluminum service-bay canopy" },
      { label: "Branding", value: "Hand-painted, commercial-grade paint" },
      { label: "Service Area", value: "South Texas" },
    ],
    outcomes: [
      "Awning doubles as branded storefront signage",
      "High-visibility yellow reads from the road in any light",
      "Hand-painted graphics built to hold color in direct Texas sun",
    ],
    images: [
      { src: bradzoil988, alt: "Bright yellow Sunbrella storefront awning with hand-painted Bradzoil 10 Minute Oil Change logo and 988 street number installed by Texas Made Shade", caption: "Bradzoil 988 — branded yellow storefront awning" },
      { src: bradzoilCanopy, alt: "Bright yellow Sunbrella canvas service-bay canopy fabricated and installed by Texas Made Shade for Bradzoil 10 Minute Oil Change", caption: "Service-bay canopy — same fabric and frame system" },
      { src: bradzoilBanner, alt: "Hand-painted commercial Bradzoil 10 Minute Oil Change banner awning by Texas Made Shade", caption: "Hand-painted commercial banner awning" },
    ],
  },
  {
    slug: "tuff-shed-storefront-awning",
    title: "Tuff Shed — Storefront Awning Re-Cover with Hand-Painted Logo",
    client: "Tuff Shed (Texas retail location)",
    location: "Texas",
    service: "Awnings",
    serviceHref: "/awnings",
    category: "Awnings",
    year: 2023,
    summary:
      "Re-cover of an existing Tuff Shed storefront awning frame with new Sunbrella® canvas and a hand-painted logo — fabricated in Seguin, installed on-site.",
    overview: [
      "Tuff Shed's existing welded frame was structurally sound but their canvas had reached the end of its UV life. We patterned the frame on-site, sewed a new Sunbrella canvas top in our Seguin shop, hand-painted the brand logo, and re-installed in a single visit.",
      "Re-covering an existing frame saved Tuff Shed roughly half the cost of a full replacement and kept the storefront branded the entire time.",
    ],
    scope: [
      "On-site patterning of existing welded frame",
      "New Sunbrella® canvas cover sewn in-house",
      "Hand-painted Tuff Shed brand logo",
      "Single-visit re-install over the existing frame",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® premium awning canvas" },
      { label: "Frame", value: "Existing welded frame re-used" },
      { label: "Branding", value: "Hand-painted brand logo" },
      { label: "Service Area", value: "Texas (statewide)" },
    ],
    outcomes: [
      "~50% savings vs. full frame replacement",
      "Fresh, branded storefront with new 8–12 year UV life",
      "Re-cover completed in a single on-site visit",
    ],
    images: [
      { src: tuffShed, alt: "Welded aluminum storefront awning with Sunbrella canvas and hand-painted logo installed by Texas Made Shade at a Tuff Shed retail location", caption: "Tuff Shed — finished storefront awning" },
      { src: handPaintedLogo, alt: "Texas Made Shade artist hand-painting a white block-letter business logo onto green Sunbrella commercial awning canvas", caption: "Hand-painted branding — Seguin shop" },
    ],
  },
  {
    slug: "phifertex-plus-sail-shade-recover-san-antonio",
    title: "Phifertex® Plus Sail Shade Re-Cover — San Antonio Backyard",
    client: "Private residence",
    location: "San Antonio, TX",
    service: "Sail Shades",
    serviceHref: "/sail-shades",
    cityHref: "/san-antonio",
    category: "Sail Shades",
    year: 2024,
    summary:
      "Faded residential sail shade brought back to life with brand-new Phifertex® Plus mesh on the original steel frame — finished in about a week.",
    overview: [
      "A San Antonio homeowner had a sun-damaged backyard sail shade with intact steel posts and footings. Replacing the entire structure wasn't necessary — just the shade fabric and the tensioning hardware.",
      "We patterned the original geometry, sewed new Phifertex Plus panels with PTFE marine-grade thread, and re-tensioned with new 316 stainless turnbuckles, D-rings and edge cable. The patio is noticeably cooler, the shade is taut again, and the existing frame stays in service for many more years.",
    ],
    scope: [
      "On-site measurement of existing geometry",
      "New Phifertex® Plus mesh sail panels (sewn in Seguin)",
      "PTFE marine-grade thread throughout",
      "Replaced edge cable, turnbuckles and D-rings (316 stainless)",
      "Half-day re-install onto existing posts",
    ],
    specs: [
      { label: "Fabric", value: "Phifertex® Plus vinyl-coated polyester mesh, ~90% UV block" },
      { label: "Thread", value: "PTFE marine-grade UV thread" },
      { label: "Hardware", value: "316 stainless turnbuckles & D-rings" },
      { label: "Frame", value: "Existing steel posts & footings re-used" },
      { label: "Timeline", value: "~1 week (½-day on-site)" },
    ],
    outcomes: [
      "Patio noticeably cooler in direct San Antonio sun",
      "No demo, no new footings — fabric & hardware swap only",
      "Marine-grade hardware engineered for long-term Texas exposure",
    ],
    testimonial: {
      quote:
        "Texas Made Shade re-covered our backyard sail with new Phifertex Plus and it looks better than the day we installed the original. Crew was on time, install was clean, and the patio is noticeably cooler.",
      author: "Jennifer M.",
      role: "Homeowner — San Antonio, TX",
    },
    images: [
      { src: sailBefore, alt: "Faded red sail shade fabric on existing steel frame in a South Texas backyard before re-cover", caption: "Before — faded red sail shade" },
      { src: sailAfter, alt: "Brand-new black Phifertex Plus sail shade installed on the same steel frame after re-cover by Texas Made Shade", caption: "After — new Phifertex® Plus on the same frame" },
      { src: sailUnderside, alt: "Underside view of a finished blue Phifertex Plus sail shade installed over a Texas patio with taut tensioning and corner hardware", caption: "Underside — taut tension & stainless corner hardware" },
    ],
  },
  {
    slug: "offshore-boat-storage-cover-hill-country",
    title: "Offshore Boat — Custom Black Marine Canvas Storage Cover, Hill Country Lakehouse",
    client: "Private boat owner",
    location: "Texas Hill Country",
    service: "Boat Covers",
    serviceHref: "/boat-covers",
    cityHref: "/corpus-christi",
    category: "Boat Covers",
    year: 2024,
    summary:
      "Custom heavy black Sunbrella® Marine full storage cover patterned to seal tight over an offshore performance boat under a Hill Country lakehouse deck.",
    overview: [
      "An offshore performance boat parked under a Hill Country lakehouse needed a tight, water-shedding storage cover that didn't trap heat or chafe the gel coat. Generic 'fits-most' covers leak at the transom and flap at trailer speed.",
      "We patterned the boat on-site, sewed a custom heavy black marine canvas cover with reinforced corners, rope channels, and tie-down loops, and finished with stainless snaps and YKK marine zippers.",
    ],
    scope: [
      "On-site patterning at the lakehouse",
      "Heavy Sunbrella® Marine canvas cover, sewn in Seguin",
      "Reinforced corners, rope channels, tie-down loops",
      "YKK marine zippers, stainless snaps",
      "Sealed transom flap to keep water off the outboards",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® Marine canvas (heavy)" },
      { label: "Hardware", value: "YKK marine zippers, stainless snaps" },
      { label: "Pattern", value: "On-site at the lakehouse" },
      { label: "Service Area", value: "Hill Country / Coastal Bend" },
    ],
    outcomes: [
      "Water-shedding fit over the entire transom and outboards",
      "Stays put on the trailer at highway speeds",
      "Marine-grade hardware that survives Texas humidity",
    ],
    images: [
      { src: blackBoatLakehouse, alt: "Custom black Sunbrella Marine full mooring cover by Texas Made Shade tied down over an offshore boat under a Texas Hill Country lakehouse deck", caption: "Lakehouse storage cover — under the deck" },
      { src: blackBoatSide, alt: "Custom black heavy-duty marine canvas full storage cover by Texas Made Shade fitted over a large offshore performance boat on a triple-axle trailer", caption: "Side view — sealed against the hull" },
      { src: blackBoatRear, alt: "Rear view of a custom black marine canvas full storage cover by Texas Made Shade sealed tight over the transom and outboards", caption: "Transom-tight rear seal" },
    ],
  },
  {
    slug: "tan-vinyl-stock-trailer-cover-ranch",
    title: "Tan Vinyl Stock Trailer Cover — Working Hill Country Ranch",
    client: "Texas working ranch",
    location: "Texas Hill Country",
    service: "Stock Trailer Covers",
    serviceHref: "/stock-trailer-covers",
    category: "Stock Trailer Covers",
    year: 2024,
    summary:
      "Long-bed gooseneck stock trailer fitted with a custom tan heavy-duty vinyl-coated polyester top, patterned and laced for daily ranch use.",
    overview: [
      "A working Hill Country ranch needed a stock trailer top that handled highway speed runs between pasture, sale barn and lease without flapping or tearing. Tan reads soft against Texas dust and reflects sun off the cattle below.",
      "We patterned the long-bed gooseneck in our Seguin shop, sewed a heavy-duty vinyl-coated polyester top with reinforced edges, and laced it down through brass grommets with shock cord so the top stays steady at speed.",
    ],
    scope: [
      "Pattern-fit to a long-bed gooseneck stock trailer",
      "Heavy-duty vinyl-coated polyester top",
      "Reinforced edges, brass grommets",
      "Shock-cord lacing to top rail for highway-speed stability",
    ],
    specs: [
      { label: "Fabric", value: "Heavy-duty vinyl-coated polyester (tan)" },
      { label: "Hardware", value: "Brass grommets, shock-cord lacing" },
      { label: "Trailer", value: "Long-bed gooseneck (any brand)" },
      { label: "Service Area", value: "Texas (statewide ranch use)" },
    ],
    outcomes: [
      "Stays tight at highway speeds",
      "Reflects sun off livestock in the bed",
      "Built for daily working-ranch use across South & Central Texas",
    ],
    images: [
      { src: tanRanchTrailer, alt: "Custom tan heavy-duty vinyl stock trailer cover by Texas Made Shade installed on a gooseneck livestock trailer parked on a Texas Hill Country ranch", caption: "Tan vinyl stock trailer top — Hill Country ranch" },
      { src: tanLongGooseneck, alt: "Brown long-bed gooseneck cattle trailer with two spare tires fitted with a tan heavy-duty vinyl top cover patterned by Texas Made Shade", caption: "Long-bed gooseneck — patterned for daily use" },
      { src: grayLacingCorner, alt: "Detail of a stock trailer cover corner with reinforced vinyl edge, brass grommets, and shock-cord lacing tied to the top rail", caption: "Corner detail — brass grommets & shock-cord lacing" },
    ],
  },
  {
    slug: "400-s-main-storefront-seguin",
    title: "400 S. Main — Custom Storefront & Half-Dome Entry Awnings, Seguin",
    client: "400 S. Main",
    location: "Seguin, TX",
    service: "Commercial Awnings",
    serviceHref: "/commercial-awnings",
    cityHref: "/seguin",
    category: "Commercial Awnings",
    year: 2023,
    summary:
      "Bold red Sunbrella® storefront awning over the entry doors plus a custom grey half-dome entry awning with hand-painted '400 S. Main' lettering.",
    overview: [
      "A signature downtown Seguin storefront with two distinct shade needs — a wide storefront awning for the main entry and a custom grey half-dome over a side entry, hand-lettered with the address.",
      "We fabricated both welded aluminum frames in our Seguin shop, sewed both Sunbrella canvas covers, and hand-painted the address lettering before install.",
    ],
    scope: [
      "Two custom welded aluminum awning frames",
      "Sunbrella® red storefront canvas + grey dome canvas",
      "Hand-painted '400 S. Main' lettering on dome",
      "On-site install over existing entries",
    ],
    specs: [
      { label: "Fabric", value: "Sunbrella® premium awning canvas (red & grey)" },
      { label: "Frames", value: "TIG-welded aluminum (storefront + dome)" },
      { label: "Branding", value: "Hand-painted address lettering" },
      { label: "Service Area", value: "Seguin / Guadalupe County" },
    ],
    outcomes: [
      "Two coordinated awnings tied together by craftsmanship",
      "Address lettering doubles as wayfinding signage",
      "Welded aluminum frames built to last 20+ years",
    ],
    images: [
      { src: mainStreetAwning, alt: "Custom welded aluminum entry awning with bold red Sunbrella canvas installed by Texas Made Shade at 400 S. Main Street in Seguin, Texas", caption: "400 S. Main — red storefront entry awning" },
      { src: mainDome, alt: "Custom grey Sunbrella half-dome entry awning with hand-painted '400 S. Main' lettering fabricated by Texas Made Shade in the Seguin shop", caption: "Grey half-dome — hand-painted address lettering" },
    ],
  },
];

export function findCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug);
}
